import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { cn } from "@/lib/utils"

/*
 * Tabs follow PADROES-UI section 1 by default: section navigation is
 * underline tabs on a gray rule (the Feed widget card's). The `segmented`
 * variant is the rare block control (a payment method picker); views and
 * filters of a list are `ViewSwitch` / `FilterPills`, not these.
 */

export type TabsVariant = "underline" | "segmented"
/** `sm` is the compact size: tabs inside a dialog or inside a section. */
export type TabsSize = "md" | "sm"

const TabsStyle = React.createContext<{ variant: TabsVariant; size: TabsSize }>({ variant: "underline", size: "md" })

/** The trigger of an underline tab, for a trigger rendered outside a `TabsList`. */
export const underlineTabTriggerClass = (size: TabsSize = "md") => cn(
  "-mb-px flex-shrink-0 gap-1.5 rounded-none border-b-2 border-transparent px-2.5 py-0 text-gray-500 hover:text-gray-900 sm:px-3",
  /* No boxed ring around an underline tab (it read as a stray colored rectangle): the keyboard focus tints the tab instead. */
  "focus-visible:rounded-t-md focus-visible:bg-gray-100 focus-visible:text-gray-900 focus-visible:ring-0 focus-visible:ring-offset-0",
  "data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-gray-900 data-[state=active]:shadow-none",
  size === "md" ? "h-11" : "h-10 px-2 text-[13px] sm:px-2.5"
)

const Tabs = TabsPrimitive.Root

interface TabsListProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  variant?: TabsVariant
  size?: TabsSize
}

/**
 * Underline: one line on a gray rule. More tabs than fit scroll sideways inside
 * the row (never the page), the active one is brought into view, and a shadow
 * marks the side that has more (PADROES-UI section 13).
 */
const TabsList = React.forwardRef<React.ElementRef<typeof TabsPrimitive.List>, TabsListProps>(
  ({ className, variant = "underline", size = "md", children, ...props }, ref) => {
    const listRef = React.useRef<HTMLDivElement | null>(null)
    const setRef = React.useCallback((node: HTMLDivElement | null) => {
      listRef.current = node
      if (typeof ref === "function") ref(node)
      else if (ref) ref.current = node
    }, [ref])
    const [edges, setEdges] = React.useState({ left: false, right: false, height: 0 })
    const underline = variant === "underline"

    React.useEffect(() => {
      const node = listRef.current
      if (!underline || !node) return
      const measure = () => {
        const left = node.scrollLeft > 1
        const right = node.scrollLeft + node.clientWidth < node.scrollWidth - 1
        const height = node.offsetHeight
        setEdges((prev) => (prev.left === left && prev.right === right && prev.height === height ? prev : { left, right, height }))
      }
      /* The active tab is always in view (a deep link to the last section on a phone). */
      const reveal = () => {
        const active = node.querySelector<HTMLElement>('[role="tab"][data-state="active"]')
        if (!active) return
        const { offsetLeft, offsetWidth } = active
        if (offsetLeft < node.scrollLeft || offsetLeft + offsetWidth > node.scrollLeft + node.clientWidth) {
          node.scrollTo({ left: Math.max(0, offsetLeft - 24) })
        }
      }
      measure()
      reveal()
      const resize = new ResizeObserver(measure)
      resize.observe(node)
      const mutations = new MutationObserver(() => { reveal(); measure() })
      mutations.observe(node, { subtree: true, childList: true, attributes: true, attributeFilter: ["data-state"] })
      node.addEventListener("scroll", measure, { passive: true })
      return () => { resize.disconnect(); mutations.disconnect(); node.removeEventListener("scroll", measure) }
    }, [underline])

    const list = (
      <TabsPrimitive.List
        ref={setRef}
        className={cn(
          underline
            ? "flex h-auto w-full items-end justify-start gap-1 overflow-x-auto rounded-none border-b border-gray-200 bg-transparent p-0 text-gray-500 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            : "inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground",
          className
        )}
        {...props}
      >
        <TabsStyle.Provider value={{ variant, size }}>{children}</TabsStyle.Provider>
      </TabsPrimitive.List>
    )
    if (!underline) return list
    return (
      <div className="relative min-w-0">
        {list}
        {/* Sized to the row, not the wrapper: a margin on the list stays clear of the shadow. */}
        {edges.left && <div aria-hidden style={{ height: edges.height }} className="pointer-events-none absolute left-0 top-0 w-px shadow-[10px_0_14px_-10px_rgba(15,23,42,0.35)]" />}
        {edges.right && <div aria-hidden style={{ height: edges.height }} className="pointer-events-none absolute right-0 top-0 w-px shadow-[-10px_0_14px_-10px_rgba(15,23,42,0.35)]" />}
      </div>
    )
  }
)
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const { variant, size } = React.useContext(TabsStyle)
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
        variant === "underline"
          ? underlineTabTriggerClass(size)
          : "rounded-md px-3 py-1 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow",
        className
      )}
      {...props}
    />
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
